import type { AdminQuestCompletionEvent } from '@src/dto/QuestCompletionEvents';
import { Store } from '@tanstack/react-store';
import type { SortingState } from '@tanstack/react-table';
import { createManagementActions } from './createManagementStore';

interface QuestCompletionEventsState {
  events: AdminQuestCompletionEvent[];
  filters: {
    seasonId: string;
    roundId: string;
    roundAppId: string;
    questId: string;
    // 'all' | 'Easy' | 'Deep'
    tier: string;
    // 'awarded' | 'all'
    completionView: string;
    developerCredentialId: string;
    search: string;
  };
  sorting: SortingState;
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
  ui: {
    isLoading: boolean;
    // The event whose details drawer is open, or null when closed.
    selectedEventId: string | null;
  };
}

const initialFilters: QuestCompletionEventsState['filters'] = {
  seasonId: '',
  roundId: '',
  roundAppId: '',
  questId: '',
  tier: 'all',
  completionView: 'awarded',
  developerCredentialId: '',
  search: '',
};

const defaultSorting: SortingState = [{ id: 'completedAt', desc: true }];

export const questCompletionEventsStore = new Store<QuestCompletionEventsState>(
  {
    events: [],
    filters: initialFilters,
    sorting: defaultSorting,
    pagination: { currentPage: 1, totalPages: 1, total: 0 },
    ui: {
      isLoading: true,
      selectedEventId: null,
    },
  },
);

const actions = createManagementActions(questCompletionEventsStore, {
  initialFilters,
  defaultSorting,
});

export const {
  updateFilter,
  setSorting,
  setPage,
  setPaginationInfo,
  setLoading,
  resetFilters,
} = actions;

export const setEvents = (events: AdminQuestCompletionEvent[]) => {
  questCompletionEventsStore.setState((prev) => ({ ...prev, events }));
};

// Update several filter keys atomically (used by the cascading season → round →
// app → quest selects) so the table only reloads once per change.
export const setFilters = (
  partial: Partial<QuestCompletionEventsState['filters']>,
) => {
  questCompletionEventsStore.setState((prev) => ({
    ...prev,
    filters: { ...prev.filters, ...partial },
    pagination: { ...prev.pagination, currentPage: 1 },
  }));
};

export const selectEvent = (eventId: string | null) => {
  actions.setUiField('selectedEventId', eventId);
};

function isSameSorting(a: SortingState, b: SortingState): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((sort, index) => {
    const other = b[index];
    return (
      other !== undefined && sort.id === other.id && sort.desc === other.desc
    );
  });
}

// Atomically replace the URL-backed slice (filters + sorting + page + the open
// detail modal) from a parsed URL. Returns the previous state untouched when
// nothing differs so the URL→store→URL sync can't loop, and preserves nested
// references so unrelated `useStore` selectors don't re-render.
export const applyQuestEventsUrlState = (next: {
  filters: QuestCompletionEventsState['filters'];
  sorting: SortingState;
  page: number;
  selectedEventId: string | null;
}) => {
  questCompletionEventsStore.setState((prev) => {
    const sameFilters =
      prev.filters.seasonId === next.filters.seasonId &&
      prev.filters.roundId === next.filters.roundId &&
      prev.filters.roundAppId === next.filters.roundAppId &&
      prev.filters.questId === next.filters.questId &&
      prev.filters.tier === next.filters.tier &&
      prev.filters.completionView === next.filters.completionView &&
      prev.filters.developerCredentialId ===
        next.filters.developerCredentialId &&
      prev.filters.search === next.filters.search;
    const sameSorting = isSameSorting(prev.sorting, next.sorting);
    const samePage = prev.pagination.currentPage === next.page;
    const sameSelected = prev.ui.selectedEventId === next.selectedEventId;

    if (sameFilters && sameSorting && samePage && sameSelected) {
      return prev;
    }

    return {
      ...prev,
      filters: sameFilters ? prev.filters : { ...next.filters },
      sorting: sameSorting ? prev.sorting : next.sorting,
      pagination: samePage
        ? prev.pagination
        : { ...prev.pagination, currentPage: next.page },
      ui: sameSelected
        ? prev.ui
        : { ...prev.ui, selectedEventId: next.selectedEventId },
    };
  });
};
